import { useMemo } from 'react';
import {
  RiCloseLine,
  RiCheckLine,
  RiAsterisk,
  RiForbid2Line,
} from '@remixicon/react';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { type RouteFunc } from '@backstage/frontend-plugin-api';
import { getCheckIcon } from '../tracks';
import {
  normalizeOwnerEntityRef,
  type CheckResult,
} from '@internal/backstage-plugin-pulse-common';
import {
  Flex,
  Table,
  useTable,
  CellText,
  Cell,
  type ColumnConfig,
  type SortDescriptor,
  SearchField,
  ToggleButtonGroup,
  ToggleButton,
  Text,
} from '@backstage/ui';
import {
  parseEntityRef,
  type CompoundEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import { toCanonicalOwnerRef } from '../insights/insights-group-scope';
import { buildEntityPulseHref } from '../entity-content';
import { paginationOptions, sortCollator } from '../config';

export type ResultsTableRow = {
  id: string;
  entityRef: string;
  checkResult?: CheckResult;
  entity?: Entity;
  status: 'passed' | 'failed' | 'excluded';
};

export type StatusFilterOption = 'affected' | 'passed' | 'failed' | 'excluded';

const getRowOwnerGroup = (item: ResultsTableRow): string | undefined => {
  const owner =
    typeof item.entity?.spec?.owner === 'string'
      ? item.entity.spec.owner
      : undefined;
  return owner ? toCanonicalOwnerRef(owner) : undefined;
};

const getRowKind = (item: ResultsTableRow): string | undefined =>
  (item.entity?.kind ?? item.entityRef.split(':')[0])?.toLowerCase() ||
  undefined;

const getRowLifecycle = (item: ResultsTableRow): string | undefined =>
  typeof item.entity?.spec?.lifecycle === 'string'
    ? item.entity.spec.lifecycle
    : undefined;

/** Distinct entity kinds of the evaluated entities. */
export const getKindOptions = (
  data: ResultsTableRow[],
): { value: string; label: string }[] =>
  Array.from(
    new Set(
      data.map(getRowKind).filter((kind): kind is string => kind !== undefined),
    ),
  )
    .sort((a, b) => a.localeCompare(b))
    .map(kind => ({
      value: kind,
      label: `${kind.charAt(0).toUpperCase()}${kind.slice(1)}`,
    }));

/** Distinct lifecycles of the evaluated entities. */
export const getLifecycleOptions = (
  data: ResultsTableRow[],
): { value: string; label: string }[] =>
  Array.from(
    new Set(
      data
        .map(getRowLifecycle)
        .filter((lifecycle): lifecycle is string => lifecycle !== undefined),
    ),
  )
    .sort((a, b) => a.localeCompare(b))
    .map(lifecycle => ({ value: lifecycle, label: lifecycle }));

export const filterRowsByGroups = (
  data: ResultsTableRow[],
  selectedGroups: string[],
): ResultsTableRow[] => {
  if (selectedGroups.length === 0) return data;
  const selected = new Set(selectedGroups);
  return data.filter(item => {
    const group = getRowOwnerGroup(item);
    return group ? selected.has(group) : false;
  });
};

export const filterRowsByKinds = (
  data: ResultsTableRow[],
  selectedKinds: string[],
): ResultsTableRow[] => {
  if (selectedKinds.length === 0) return data;
  return data.filter(item => {
    const kind = getRowKind(item);
    return kind ? selectedKinds.includes(kind) : false;
  });
};

export const filterRowsByLifecycles = (
  data: ResultsTableRow[],
  selectedLifecycles: string[],
): ResultsTableRow[] => {
  if (selectedLifecycles.length === 0) return data;
  return data.filter(item => {
    const lifecycle = getRowLifecycle(item);
    return lifecycle ? selectedLifecycles.includes(lifecycle) : false;
  });
};

const getStatusValue = (item: ResultsTableRow): string => item.status;

const getEntityNameValue = (item: ResultsTableRow): string =>
  item.entity?.metadata?.title ||
  item.entity?.metadata?.name ||
  item.entityRef.split('/')[1] ||
  item.entityRef;

const getKindValue = (item: ResultsTableRow): string =>
  item.entity?.kind || item.entityRef.split(':')[0] || '';

const getTypeValue = (item: ResultsTableRow): string =>
  typeof item.entity?.spec?.type === 'string' ? item.entity.spec.type : '';

const getOwnerValue = (item: ResultsTableRow): string =>
  typeof item.entity?.spec?.owner === 'string' ? item.entity.spec.owner : '';

const getSortableValue = (item: ResultsTableRow, column: string): string => {
  switch (column) {
    case 'status':
      return getStatusValue(item);
    case 'entityName':
      return getEntityNameValue(item);
    case 'kind':
      return getKindValue(item);
    case 'type':
      return getTypeValue(item);
    case 'owner':
      return getOwnerValue(item);
    default:
      return '';
  }
};

const sortRows = (
  data: ResultsTableRow[],
  sort: SortDescriptor,
): ResultsTableRow[] => {
  const direction = sort.direction === 'descending' ? -1 : 1;
  const sortColumn = String(sort.column);

  return [...data].sort((left, right) => {
    const comparison = sortCollator.compare(
      getSortableValue(left, sortColumn).trim(),
      getSortableValue(right, sortColumn).trim(),
    );

    if (comparison !== 0) {
      return comparison * direction;
    }

    return sortCollator.compare(left.id, right.id);
  });
};

export const createColumns = (
  entityRoute: RouteFunc<CompoundEntityRef> | undefined,
  checkId?: string,
): ColumnConfig<ResultsTableRow>[] => [
  {
    id: 'entityName',
    label: 'Entity',
    isRowHeader: true,
    defaultWidth: '50fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={item.entity?.metadata?.title || item.entityRef.split('/')[1]}
        leadingIcon={getCheckIcon({ checkResult: item.checkResult, size: 20 })}
        href={
          entityRoute
            ? buildEntityPulseHref(
                entityRoute(parseEntityRef(item.entityRef)),
                {
                  checkId,
                },
              )
            : undefined
        }
      />
    ),
  },
  {
    id: 'kind',
    label: 'Kind',
    defaultWidth: '15fr',
    isSortable: true,
    cell: item => <CellText title={item.entityRef.split(':')[0]} />,
  },
  {
    id: 'type',
    label: 'Type',
    defaultWidth: '15fr',
    isSortable: true,
    cell: item => (
      <CellText title={item.entity?.spec?.type as string} color="secondary" />
    ),
  },
  {
    id: 'lifecycle',
    label: 'Lifecycle',
    defaultWidth: '15fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={item.entity?.spec?.lifecycle as string}
        color="secondary"
      />
    ),
  },
  {
    id: 'owner',
    label: 'Owner',
    defaultWidth: '20fr',
    isSortable: true,
    cell: item => {
      let owner: string | undefined;

      if (typeof item.entity?.spec?.owner === 'string') {
        owner = item.entity.spec.owner;
      }

      const ownerEntityRef = owner ? normalizeOwnerEntityRef(owner) : undefined;
      if (ownerEntityRef) {
        return (
          <Cell>
            <EntityRefLink entityRef={ownerEntityRef} underline="none" />
          </Cell>
        );
      }

      return owner ? (
        <CellText title={owner} />
      ) : (
        <CellText title="-" color="secondary" />
      );
    },
  },
];

type ResultsTableProps = {
  data: ResultsTableRow[] | undefined;
  error: Error | undefined;
  columns: ColumnConfig<ResultsTableRow>[];
  statusFilter: StatusFilterOption;
  onStatusFilterChange: (filter: StatusFilterOption) => void;
};

export const ResultsTable = ({
  data,
  error,
  columns,
  statusFilter,
  onStatusFilterChange,
}: ResultsTableProps) => {
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (statusFilter === 'affected') {
      return data.filter(item => item.status !== 'excluded');
    }
    if (statusFilter === 'passed') {
      return data.filter(item => item.status === 'passed');
    }
    if (statusFilter === 'failed') {
      return data.filter(item => item.status === 'failed');
    }
    if (statusFilter === 'excluded') {
      return data.filter(item => item.status === 'excluded');
    }
    return [];
  }, [data, statusFilter]);

  const { tableProps, search } = useTable({
    mode: 'complete',
    data: filteredData,
    initialSort: { column: 'entityName', direction: 'ascending' },
    sortFn: sortRows,
    searchFn: (items, query) => {
      const lowerQuery = query.toLowerCase();
      return items.filter(
        item =>
          item.entityRef.toLowerCase().includes(lowerQuery) ||
          item.entity?.metadata?.title?.toLowerCase().includes(lowerQuery) ||
          item.entity?.metadata?.name?.toLowerCase().includes(lowerQuery),
      );
    },
    paginationOptions,
  });

  return (
    <Flex direction="column" gap="4">
      <Flex direction="row" gap="4" justify="between" align="center">
        <Text variant="title-x-small">Entities ({filteredData.length})</Text>

        <Flex gap="4" align="center">
          <ToggleButtonGroup
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={new Set([statusFilter])}
            onSelectionChange={selected => {
              const nextFilter = Array.from(selected)[0] as
                | StatusFilterOption
                | undefined;

              onStatusFilterChange(nextFilter ?? 'affected');
            }}
            aria-label="Filter by status"
          >
            <ToggleButton
              key="affected"
              id="affected"
              size="small"
              iconStart={<RiAsterisk />}
            >
              All affected
            </ToggleButton>
            <ToggleButton
              key="passed"
              id="passed"
              size="small"
              iconStart={<RiCheckLine />}
            >
              Passed
            </ToggleButton>
            <ToggleButton
              key="failed"
              id="failed"
              size="small"
              iconStart={<RiCloseLine />}
            >
              Failed
            </ToggleButton>
            <ToggleButton
              key="excluded"
              id="excluded"
              size="small"
              iconStart={<RiForbid2Line />}
            >
              Excluded
            </ToggleButton>
          </ToggleButtonGroup>
          <SearchField
            aria-label="Search entities"
            placeholder="Search..."
            {...search}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            style={{ maxWidth: '256px' }}
          />
        </Flex>
      </Flex>

      <Table
        columnConfig={columns}
        {...tableProps}
        emptyState="No entities found for this check"
        error={error}
      />
    </Flex>
  );
};
